import { Checkbox } from "antd";
import { calculateElrGroupsForVideo } from "components/util/Geometry";
import { getRoutePosAtTimestamp } from "components/util/GeometryUtilsV2";
import LoadingOverlay from "components/util/LoadingOverlay";
import { binarySearch, videoKeyToTimestamp } from "components/util/PlaylistUtils";
import { init as ecInit } from "echarts";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Measure from "react-measure";
import { useDispatch, useSelector } from "react-redux";
import { routeSelected, websocketMessage } from "redux/actions";
import { routeGeomSelector, sourceOffsetSelector } from "redux/selectors";
import { boundedRange, boundedValue, useMouseMovement, useOnClick } from "../ChartV2/utils";
import { calculateGridHeight, option } from "./chartOption";

const MIN_ZOOM = 15 * 1000; // 15 seconds
const MAX_ZOOM = 3 * 60 * 1000; // 3 minutes

const currentTimestampSelector = (state) => _.get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "timestamp"], 0);
const isPlayingSelector = (state) => _.get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "playerState"], "paused") === "playing";
const routeSystemIDSelector = (state) => state.playlist.data.system_id;
const playlistVideoSelector = (state) =>
    _.get(state.playlist.data.video, [_.get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "sourceIndex"], 0)], []);
const userConfigSelector = (state) => state.userDetails.userConfig;
const sessionIdSelector = (state) => state.playlist.data.routeID;
const sessionKeySelector = (state) => _.get(state.sessions, [state.playlist.data.routeID, "uuid"]);
const sessionObservationsSelector = (state) => state.sessionObservations;

const extractAnalysisResultsForKeys = (keys, analysisResults, startIndex, endIndex) => {
    const results = Object.fromEntries(keys.map((k) => [k, []]));

    for (let i = startIndex; i < endIndex; i++) {
        const p = analysisResults[i];

        for (const key of keys) {
            results[key].push([p.ts, p[key]]);
        }
    }

    return results;
};

const filterExceedanceDataForType = (observations, type, stream, zoomBounds) => {
    return observations
        .filter((o) => o.timestamp >= zoomBounds[0] && o.timestamp <= zoomBounds[1] && o.class === type && o.stream === stream)
        .map((o) => ({
            startTimestamp: o.start_timestamp,
            peakTimestamp: o.timestamp,
            endTimestamp: o.end_timestamp,
            exceedance: o.exceedance,
        }));
};

const dataMetrics = [
    { metric: "height", key: "vertical_position", exceedanceClass: "shoe_vertical_exceedance" },
    { metric: "stagger", key: "horizontal_shoe_rail_delta", exceedanceClass: "shoe_horizontal_exceedance" },
    { metric: "acceleration", key: "vertical_acceleration", exceedanceClass: "shoe_vertical_acceleration_exceedance" },
];

const generateMarkLine = (min, max, showSeparateChannels, dashOffset = 0) => {
    const style = showSeparateChannels ? {} : { type: [10, 20], dashOffset };

    return [
        { name: "Min", yAxis: min, lineStyle: style },
        { name: "Max", yAxis: max, lineStyle: style },
    ];
};

const Shoecam = () => {
    const dispatch = useDispatch();
    const currentTimestamp = useSelector(currentTimestampSelector);
    const isPlaying = useSelector(isPlayingSelector);
    const routeGeometry = useSelector(routeGeomSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);
    const playlistVideo = useSelector(playlistVideoSelector);
    const userConfig = useSelector(userConfigSelector);
    const sourceOffset = useSelector(sourceOffsetSelector);
    const sessionId = useSelector(sessionIdSelector);
    const sessionKey = useSelector(sessionKeySelector);
    const sessionObservations = useSelector(sessionObservationsSelector);

    const chartElRef = useRef(null);
    const chartRef = useRef(null);
    const maxBoundsRef = useRef([null, null]);

    const [initialised, setInitialised] = useState(false);
    const [hasTraceData, setHasTraceData] = useState(false);
    const [data, setData] = useState({ left: { analysis_results: [] }, right: { analysis_results: [] } });
    const [zoomBounds, setZoomBounds] = useState([null, null]);
    const [selectedTimestamp, setSelectedTimestamp] = useState(0);
    const [showSeparateChannels, setShowSeparateChannels] = useState(false);
    const [streamConfig] = useState({ left: 0, right: 1 });

    useEffect(() => {
        if (!(sessionId && sessionKey)) return;

        dispatch(routeSelected(null));
        setHasTraceData(false);
        setInitialised(false);
        setData({ left: { analysis_results: [] }, right: { analysis_results: [] } });
        setZoomBounds([null, null]);
        setSelectedTimestamp(0);
        maxBoundsRef.current = [null, null];

        const createUrlWithSide = (side) => `https://shoe.aivr.video/analysis/${sessionKey}/shoecam_analysis_results_${sessionId}_s${side}.json`;

        const fetchOptions = {
            mode: "cors",
            headers: { "Content-Type": "application/json" },
        };

        Promise.all([fetch(createUrlWithSide(streamConfig.left), fetchOptions), fetch(createUrlWithSide(streamConfig.right), fetchOptions)])
            .then((responses) => {
                const promises = responses.map((response) => (response.ok ? response.json() : null));

                if (promises.every((promise) => promise === null)) {
                    return Promise.reject(responses);
                }

                setHasTraceData(true);
                return Promise.all(promises);
            })
            .then(([leftJson, rightJson]) => {
                // // Used to create test data for the right channel
                // if (leftJson !== null) {
                //     rightJson = _.cloneDeep(leftJson)
                //     rightJson.analysis_results = rightJson.analysis_results.map((d, i, orig) => {
                //         const lastIndex = orig.length - 1;

                //         return {
                //             ts: d.ts,
                //             vertical_position: orig[lastIndex - i].vertical_position,
                //             horizontal_shoe_rail_delta: orig[lastIndex - i].horizontal_shoe_rail_delta,
                //             vertical_velocity: orig[lastIndex - i].vertical_velocity,
                //             vertical_acceleration: orig[lastIndex - i].vertical_acceleration,
                //         };
                //     });
                // }

                setData({
                    left: leftJson ?? { analysis_results: [] },
                    right: rightJson ?? { analysis_results: [] },
                });
            })
            .catch((error) => {
                setHasTraceData(false);

                console.error("There was an error loading the trace data", error);
            })
            .finally(() => {
                setInitialised(true);
            });
    }, [dispatch, sessionId, sessionKey, streamConfig.left, streamConfig.right]);

    const viewBounds = useCallback((bounds) => {
        if (!chartRef.current) return;

        const boundedHighlight = boundedRange(maxBoundsRef.current[0], bounds[0], bounds[1], maxBoundsRef.current[1]);
        chartRef.current.setOption({
            xAxis: (chartRef.current.getOption().xAxis ?? [])
                .filter((axis) => axis.id !== "overview_x")
                .map((axis) => ({
                    id: axis.id,
                    min: boundedHighlight[0],
                    max: boundedHighlight[1],
                })),
            series: [
                {
                    id: "current_view_series",
                    data: [boundedHighlight],
                },
            ],
        });
    }, []);

    useEffect(() => {
        if (!playlistVideo || playlistVideo.length === 0) return;

        const sessionMinTimestamp = videoKeyToTimestamp(playlistVideo[0][0]);
        const sessionMaxTimestamp = videoKeyToTimestamp(playlistVideo[playlistVideo.length - 1][0]);
        maxBoundsRef.current = [sessionMinTimestamp, sessionMaxTimestamp];

        let minZoomTimestamp = sessionMinTimestamp;
        let maxZoomTimestamp = minZoomTimestamp + MAX_ZOOM;

        if (currentTimestamp > 0) {
            const halfSpan = MAX_ZOOM / 2;
            minZoomTimestamp = currentTimestamp - halfSpan;
            maxZoomTimestamp = currentTimestamp + halfSpan;
        }

        const newBounds = boundedRange(sessionMinTimestamp, minZoomTimestamp, maxZoomTimestamp, sessionMaxTimestamp);

        setZoomBounds(newBounds);
        viewBounds(newBounds);
        setSelectedTimestamp(currentTimestamp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playlistVideo]);

    useEffect(() => {
        if (sessionId && currentTimestamp > 0) {
            dispatch(routeSelected(sessionId, 0));
        }
    }, [dispatch, sessionId, currentTimestamp]);

    useEffect(() => {
        if (!chartElRef.current) return;

        const chart = ecInit(chartElRef.current, "dark", {
            renderer: "canvas",
            animation: false,
        });

        chart.setOption(option);
        chartRef.current = chart;

        return () => {
            chart.dispose();
            chartRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (currentTimestamp) {
            setSelectedTimestamp(currentTimestamp);
        }
    }, [currentTimestamp]);

    const routeIndexes = useMemo(() => {
        const routePositionData = _.get(routeGeometry, ["distance_to_route_position"], []);

        return routePositionData.length > 0 ? calculateElrGroupsForVideo(playlistVideo, routeGeometry, routeSystemID) : null;
    }, [playlistVideo, routeGeometry, routeSystemID]);

    useEffect(() => {
        if (!chartRef.current || !routeIndexes) return;

        chartRef.current.setOption(
            {
                dataset: [
                    {
                        id: "overview_dataset",
                        source: routeIndexes,
                    },
                ],
            },
            false,
        );
    }, [routeIndexes]);

    const observations = useMemo(() => {
        if (!sessionObservations) return [];

        return sessionObservations.filter((o) => o.observation_type === "observation_shoecam");
    }, [sessionObservations]);

    useEffect(() => {
        if (!chartRef.current) return;

        chartRef.current.setOption({
            dataset: [
                {
                    id: "arcing_dataset",
                    source: observations
                        .filter((o) => o.class === "shoe_arc_exceedance")
                        .map((o) => ({
                            peakTimestamp: o.timestamp,
                        })),
                },
            ],
        });
    }, [observations]);

    const updateCurrentPosition = useCallback(
        (timestamp, zoomBounds, isPlaying = false) => {
            if (!chartRef.current) return;

            if (isPlaying) {
                const range = zoomBounds[1] - zoomBounds[0];
                const [sessionMinTimestamp, sessionMaxTimestamp] = maxBoundsRef.current;

                if (timestamp < zoomBounds[0] || timestamp > zoomBounds[1]) {
                    const newBounds = boundedRange(sessionMinTimestamp, timestamp - range / 2, timestamp + range / 2, sessionMaxTimestamp);
                    setZoomBounds(newBounds);
                    viewBounds(newBounds);
                }
            }

            chartRef.current.setOption({
                series: [
                    {
                        id: "selected_timestamp_position_series",
                        data: [[timestamp]],
                    },
                    {
                        id: "selected_timestamp_overview_series",
                        data: [[timestamp]],
                    },
                ],
            });
        },
        [viewBounds],
    );

    useEffect(() => {
        updateCurrentPosition(selectedTimestamp, zoomBounds, isPlaying);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTimestamp]);

    useEffect(() => {
        if (!chartRef.current) return;

        const range = (zoomBounds[1] - zoomBounds[0]) / 2;

        const startIndexLeft = binarySearch(zoomBounds[0] - range, data.left.analysis_results, (geom) => geom.ts);
        const endIndexLeft = binarySearch(zoomBounds[1] + range, data.left.analysis_results, (geom) => geom.ts);

        const startIndexRight = binarySearch(zoomBounds[0] - range, data.right.analysis_results, (geom) => geom.ts);
        const endIndexRight = binarySearch(zoomBounds[1] + range, data.right.analysis_results, (geom) => geom.ts);

        const keys = dataMetrics.map((m) => m.key);
        const analysisResults = {
            left: extractAnalysisResultsForKeys(keys, data.left.analysis_results, startIndexLeft, endIndexLeft),
            right: extractAnalysisResultsForKeys(keys, data.right.analysis_results, startIndexRight, endIndexRight),
        };

        chartRef.current.setOption({
            dataset: Object.keys(streamConfig).flatMap((channel) =>
                dataMetrics.map(({ metric, exceedanceClass }) => ({
                    id: `${channel}_${metric}_exceedance_dataset`,
                    source: filterExceedanceDataForType(observations, exceedanceClass, streamConfig[channel], zoomBounds),
                })),
            ),
            series: Object.keys(streamConfig).flatMap((channel) =>
                dataMetrics.map(({ metric, key }) => ({
                    id: `${channel}_${metric}_series`,
                    data: analysisResults[channel][key],
                })),
            ),
        });
    }, [data.left.analysis_results, data.right.analysis_results, observations, streamConfig, zoomBounds]);

    useEffect(() => {
        if (!chartRef.current) return;

        const minHeightThreshold = 480;
        const maxHeightThreshold = 810;
        const defaultStaggerThreshold = 300;
        const defaultVerticalAccelerationThreshold = 7500;

        const metricConfig = [
            {
                label: "height",
                key: "vertical_position",
                getMinMax: (channelData) => [channelData.shoe_min_elevation ?? minHeightThreshold, channelData.shoe_max_elevation ?? maxHeightThreshold],
            },
            {
                label: "stagger",
                key: "horizontal_shoe_rail_delta",
                getMinMax: (channelData) => [
                    -(channelData.shoe_max_delta_from_rail ?? defaultStaggerThreshold),
                    channelData.shoe_max_delta_from_rail ?? defaultStaggerThreshold,
                ],
            },
            {
                label: "acceleration",
                key: "vertical_acceleration",
                getMinMax: () => [-defaultVerticalAccelerationThreshold, defaultVerticalAccelerationThreshold],
            },
        ];

        chartRef.current.setOption({
            series: ["left", "right"].flatMap((channel, channelIndex) =>
                metricConfig.map((metric) => {
                    const [min, max] = metric.getMinMax(data[channel]);
                    const dashOffset = channelIndex === 1 ? 15 : 0;

                    return {
                        id: `${channel}_${metric.label}_series`,
                        markLine: { data: generateMarkLine(min, max, showSeparateChannels, dashOffset) },
                    };
                }),
            ),
        });
    }, [data, showSeparateChannels]);

    useEffect(() => {
        if (!chartRef.current) return;

        const gridHeight = `${calculateGridHeight(showSeparateChannels)}%`;

        chartRef.current.setOption({
            grid: [
                { id: "height_grid", height: gridHeight },
                { id: "stagger_grid", height: gridHeight },
                { id: "velocity_grid", height: gridHeight },
                { id: "acceleration_grid", height: gridHeight },
            ],
            yAxis: [
                {
                    id: "height_y",
                    name: showSeparateChannels ? "Left Height (px)" : "Height (px)",
                },
                {
                    id: "right_height_y",
                    show: showSeparateChannels,
                },
                {
                    id: "stagger_y",
                    name: showSeparateChannels ? "Left Stagger (px)" : "Stagger (px)",
                },
                {
                    id: "right_stagger_y",
                    show: showSeparateChannels,
                },
                {
                    id: "acceleration_y",
                    name: showSeparateChannels ? "Left V. Accel px/s²" : "Vertical Acceleration (px/s²)",
                },
                {
                    id: "right_acceleration_y",
                    show: showSeparateChannels,
                },
            ],
            series: [
                {
                    id: "right_height_series",
                    xAxisId: showSeparateChannels ? "right_height_x" : "height_x",
                    yAxisId: showSeparateChannels ? "right_height_y" : "height_y",
                },
                {
                    id: "right_stagger_series",
                    xAxisId: showSeparateChannels ? "right_stagger_x" : "stagger_x",
                    yAxisId: showSeparateChannels ? "right_stagger_y" : "stagger_y",
                },
                {
                    id: "right_acceleration_series",
                    xAxisId: showSeparateChannels ? "right_acceleration_x" : "acceleration_x",
                    yAxisId: showSeparateChannels ? "right_acceleration_y" : "acceleration_y",
                },
                {
                    id: "right_height_exceedance_series",
                    xAxisId: showSeparateChannels ? "right_height_x" : "height_x",
                    yAxisId: showSeparateChannels ? "right_height_y" : "height_y",
                },
                {
                    id: "right_stagger_exceedance_series",
                    xAxisId: showSeparateChannels ? "right_stagger_x" : "stagger_x",
                    yAxisId: showSeparateChannels ? "right_stagger_y" : "stagger_y",
                },
                {
                    id: "right_acceleration_exceedance_series",
                    xAxisId: showSeparateChannels ? "right_acceleration_x" : "acceleration_x",
                    yAxisId: showSeparateChannels ? "right_acceleration_y" : "acceleration_y",
                },
            ],
        });
    }, [showSeparateChannels]);

    const tooltipFormatter = useCallback(
        (params) => {
            if (!params || params.length === 0) {
                return null;
            }

            const validParams = params.filter((p) => p.data && p.data[1] !== null);

            if (validParams.length === 0) {
                return null;
            }

            let positionString = "";
            const calculatedELR = getRoutePosAtTimestamp(validParams[0].axisValue, routeGeometry, routeSystemID);
            if (calculatedELR) {
                positionString = calculatedELR.to_string(userConfig.elr_units, sourceOffset);
            }

            return `<div class="Tooltip">
                <div><span><strong>${positionString}</strong></span></div>
                ${validParams
                    .map((p) => {
                        return `<div>
                            <div>
                                ${p.marker}
                                <span>${p.seriesName}</span>
                            </div>
                            <span><strong>${p.data[1] ? Math.round(p.data[1]) : null}</strong>px</span>
                        </div>`;
                    })
                    .join("")}
            </div>`;
        },
        [routeGeometry, routeSystemID, sourceOffset, userConfig.elr_units],
    );

    useEffect(() => {
        if (!chartRef.current) return;
        chartRef.current.setOption(
            {
                tooltip: {
                    formatter: tooltipFormatter,
                },
            },
            false,
        );
    }, [tooltipFormatter]);

    const onResize = useMemo(
        () =>
            _.debounce(() => {
                if (chartRef.current) {
                    chartRef.current.resize();
                }
            }, 500),
        [],
    );

    const onSelectTimestamp = useCallback(
        (timestamp) => {
            setSelectedTimestamp(timestamp);
            dispatch(websocketMessage({ action: "request_position", timestamp: timestamp }));
        },
        [dispatch],
    );

    const onPositionGridClick = useCallback(
        (item) => {
            const timestamp = item.position_grid[0];
            onSelectTimestamp(timestamp);
        },
        [onSelectTimestamp],
    );

    useOnClick(chartRef.current, ["position_grid"], onPositionGridClick);

    const selectBounds = useCallback(
        (bounds) => {
            setZoomBounds(bounds);
            viewBounds(bounds);
        },
        [viewBounds],
    );

    const goToTimestamp = useCallback(
        (timestamp) => {
            onSelectTimestamp(timestamp);
            const halfSpan = (zoomBounds[1] - zoomBounds[0]) / 2;
            const newBounds = boundedRange(maxBoundsRef.current[0], timestamp - halfSpan, timestamp + halfSpan, maxBoundsRef.current[1]);
            selectBounds(newBounds);
        },
        [onSelectTimestamp, selectBounds, zoomBounds],
    );

    const dragCallback = useCallback(
        (dragState) => {
            if (dragState.type !== "drag") return;

            const coordinates = dragState.current;
            const timestamp = coordinates[0];
            const newBounds = boundedRange(maxBoundsRef.current[0], timestamp - MAX_ZOOM / 2, timestamp + MAX_ZOOM / 2, maxBoundsRef.current[1]);
            viewBounds(newBounds);
        },
        [viewBounds],
    );

    const endDragCallback = useCallback(
        (dragState) => {
            if (dragState.type !== "drag") return;

            const coordinates = dragState.current;
            goToTimestamp(coordinates[0]);
        },
        [goToTimestamp],
    );

    useMouseMovement(chartRef.current, "overview_grid", dragCallback, endDragCallback);

    const calculateNewBounds = (dragState, zoomBounds, maxBounds) => {
        const [currentMin, currentMax] = zoomBounds;
        const [minBound, maxBound] = maxBounds;

        if (dragState.delta[2]) {
            // Zoom
            const timestamp = dragState.start[0];
            const range = currentMax - currentMin;

            let newRange = range * Math.pow(4, dragState.delta[2]);

            // Clamp range
            newRange = boundedValue(MIN_ZOOM, newRange, MAX_ZOOM);

            const ratio = (timestamp - currentMin) / range;

            let newStart = timestamp - ratio * newRange;
            let newEnd = newStart + newRange;

            if (newStart < minBound) {
                newStart = minBound;
                newEnd = newStart + newRange;
            }
            if (newEnd > maxBound) {
                newEnd = maxBound;
                newStart = maxBound - newRange;
            }

            return [newStart, newEnd];
        } else {
            // Pan
            const timestampDelta = Math.round(dragState.delta[0]);

            let newStart = currentMin + timestampDelta;
            let newEnd = currentMax + timestampDelta;

            if (newStart < minBound) {
                newStart = minBound;
                newEnd = newStart + (currentMax - currentMin);
            }

            if (newEnd > maxBound) {
                newEnd = maxBound;
                newStart = maxBound - (currentMax - currentMin);
            }

            return [newStart, newEnd];
        }
    };

    const boundedZoomRange = (min, low, high, max) => {
        let newLow = Math.max(min, low);
        let newHigh = Math.min(max, high);

        let range = newHigh - newLow;

        if (range < MIN_ZOOM) {
            // Expand to minimum range
            const center = newLow + range / 2;
            newLow = Math.max(min, center - MIN_ZOOM / 2);
            newHigh = Math.min(max, center + MIN_ZOOM / 2);
        } else if (range > MAX_ZOOM) {
            // Reduce to maximum range
            const center = newLow + range / 2;
            newLow = Math.max(min, center - MAX_ZOOM / 2);
            newHigh = Math.min(max, center + MAX_ZOOM / 2);
        }

        return [newLow, newHigh];
    };

    const positionDragCallback = useCallback(
        (dragState) => {
            if (chartRef.current) {
                const newBounds = calculateNewBounds(dragState, zoomBounds, maxBoundsRef.current);

                viewBounds(newBounds);
            }
        },
        [viewBounds, zoomBounds],
    );

    const positionEndDragCallback = useCallback(
        (dragState) => {
            if (chartRef.current) {
                let newBounds = calculateNewBounds(dragState, zoomBounds, maxBoundsRef.current);
                newBounds = boundedZoomRange(maxBoundsRef.current[0], newBounds[0], newBounds[1], maxBoundsRef.current[1]);

                selectBounds(newBounds);
            }
        },
        [selectBounds, zoomBounds],
    );

    useMouseMovement(chartRef.current, "position_grid", positionDragCallback, positionEndDragCallback);

    return (
        <div className="ShoecamContainer">
            <Measure
                bounds
                onResize={onResize}>
                {({ measureRef }) => (
                    <div
                        className="ShoecamInner"
                        ref={measureRef}>
                        <div
                            className={`Shoecam${!initialised || !hasTraceData ? " hidden" : ""}`}
                            ref={chartElRef}></div>
                        {!initialised && (
                            <div className="overlay">
                                <LoadingOverlay loading={true} />
                            </div>
                        )}
                        {initialised && !hasTraceData && (
                            <div className="overlay widget__Info center">
                                <p>No data for this session.</p>
                            </div>
                        )}
                    </div>
                )}
            </Measure>
            {initialised && hasTraceData && (
                <div style={{ position: "absolute", top: 0, left: 0, padding: "6px 12px" }}>
                    <label>
                        Separate channels
                        <Checkbox
                            checked={showSeparateChannels}
                            style={{ marginLeft: 6 }}
                            onChange={() => {
                                setShowSeparateChannels(!showSeparateChannels);
                            }}
                        />
                    </label>
                </div>
            )}
        </div>
    );
};

export default Shoecam;
